import {
  EMPTY_CANVAS,
  type Note,
  type Notebook,
  type NoteIndexEntry,
  type NoteSummary,
  type NotesQuery,
  type NotesSync,
  type SyncNotebook,
  type User,
} from "@ronin/contracts";
import {
  type Change,
  conflictTitle,
  type LocalNoteText,
  localBacklinks,
  matchTitles,
  type NotebookFields,
  type NoteFields,
  notebookDescendants,
  notePreview,
  type OutboxEntry,
  outboxOrder,
  queueChange,
  resolveLocalLinks,
  searchLocalNotes,
  type TrashEntry,
  trashEntries,
} from "@ronin/core";
import type { Persistence, Table, Write } from "./persistence.js";

/** A note in the offline copy (spec11 FR3), in the trash when `deletedAt` is set. */
export interface LocalNote extends NoteIndexEntry {
  /** Markdown text or canvas strokes; null while not loaded yet. */
  content: string | null;
  /**
   * The oldest version from which every later version came from this device. An editor
   * that loaded a version at or after it may save on top of the newest one; an older base
   * means someone else changed the note in between (a conflict at the next push).
   */
  ownBase: number;
}

export type LocalNotebook = SyncNotebook;
export type NotebookEntry = OutboxEntry<NotebookFields>;

export interface Meta {
  serverUrl: string;
  user: User;
  /** `serverTime` of the last complete pull. */
  lastSync: string | null;
}

/** FR8: the server's version stays in the original, the device's went into the copy. */
export interface Conflict {
  originalId: string;
  copyId: string;
  at: string;
}

export type NoteSyncState =
  | { state: "synced" }
  | { state: "pending" }
  | { state: "failed"; error: string };

/** Items created on the device have version 0 until the server confirms them. */
const UNSENT = 0;

const isLive = (item: { deletedAt: string | null }) => item.deletedAt === null;

export function createOfflineStore(
  persistence: Persistence,
  options: { now?: () => Date; newId: () => string; timeZone: () => string },
) {
  const now = options.now ?? (() => new Date());
  let meta: Meta | null = null;
  const notes = new Map<string, LocalNote>();
  const notebooks = new Map<string, LocalNotebook>();
  const outbox = new Map<string, OutboxEntry>();
  const notebookOutbox = new Map<string, NotebookEntry>();
  const conflicts = new Map<string, Conflict>();
  /** Notes that vanished (purged elsewhere, or re-created under a new id): the newest copy. */
  const gone = new Map<string, LocalNote>();
  const redirects = new Map<string, string>();

  let version = 0;
  const listeners = new Set<() => void>();
  let writing: Promise<void> = Promise.resolve();

  const maps: Record<Exclude<Table, "meta">, Map<string, unknown>> = {
    notes,
    notebooks,
    outbox,
    notebookOutbox,
    conflicts,
  };

  /** Applies a batch to memory at once, notifies, and persists in order behind it. */
  function commit(writes: Write[]) {
    if (writes.length === 0) return;
    for (const { table, key, value } of writes) {
      if (table === "meta") meta = (value as Meta | undefined) ?? null;
      else if (value === undefined) maps[table].delete(key);
      else maps[table].set(key, value);
    }
    version++;
    for (const listener of listeners) listener();
    writing = writing
      .then(() => persistence.write(writes))
      // Never log the content: only that a write failed.
      .catch((error: unknown) =>
        console.error("Offline store write failed", error instanceof Error ? error.name : ""),
      );
  }

  const putNote = (note: LocalNote): Write => ({ table: "notes", key: note.id, value: note });
  const dropNote = (id: string): Write => ({ table: "notes", key: id, value: undefined });
  const putNotebook = (nb: LocalNotebook): Write => ({
    table: "notebooks",
    key: nb.id,
    value: nb,
  });
  const dropNotebook = (id: string): Write => ({ table: "notebooks", key: id, value: undefined });
  const putEntry = (id: string, entry: OutboxEntry | null): Write => ({
    table: "outbox",
    key: id,
    value: entry ?? undefined,
  });
  const putNotebookEntry = (id: string, entry: NotebookEntry | null): Write => ({
    table: "notebookOutbox",
    key: id,
    value: entry ?? undefined,
  });
  const dropConflict = (copyId: string): Write => ({
    table: "conflicts",
    key: copyId,
    value: undefined,
  });

  const queue = (id: string, change: Change<NoteFields>) =>
    putEntry(id, queueChange(outbox.get(id), id, change, now()));
  const queueNotebook = (id: string, change: Change<NotebookFields>) =>
    putNotebookEntry(id, queueChange(notebookOutbox.get(id), id, change, now()));

  const liveNotes = () => [...notes.values()].filter(isLive);
  const texts = (): LocalNoteText[] => liveNotes();
  const target = (id: string) => redirects.get(id) ?? id;
  const preview = (kind: LocalNote["kind"], content: string) =>
    kind === "canvas" ? "" : notePreview(content);

  /** Conflict pairs that involve one of `ids` (resolved by deleting a side). */
  const conflictsOf = (ids: ReadonlySet<string>) =>
    [...conflicts.values()]
      .filter((c) => ids.has(c.originalId) || ids.has(c.copyId))
      .map((c) => dropConflict(c.copyId));

  /** A new note on the device, queued as a create. */
  function newLocalNote(fields: {
    kind?: LocalNote["kind"];
    title: string;
    content: string;
    notebookId?: string | null;
    pinned?: boolean;
  }) {
    const at = now().toISOString();
    const id = options.newId();
    const kind = fields.kind ?? "markdown";
    const note: LocalNote = {
      id,
      kind,
      title: fields.title,
      preview: preview(kind, fields.content),
      content: fields.content,
      notebookId: fields.notebookId ?? null,
      pinned: fields.pinned ?? false,
      createdAt: at,
      updatedAt: at,
      deletedAt: null,
      version: UNSENT,
      ownBase: UNSENT,
    };
    const writes = [
      putNote(note),
      queue(id, {
        op: "create",
        fields: {
          kind,
          title: note.title,
          content: fields.content,
          notebookId: note.notebookId,
          ...(note.pinned && { pinned: true }),
        },
      }),
    ];
    return { note, writes };
  }

  function summary(note: LocalNote): NoteSummary {
    return {
      id: note.id,
      kind: note.kind,
      title: note.title,
      preview: note.preview,
      notebookId: note.notebookId,
      pinned: note.pinned,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
    };
  }

  function notebookWithCount(nb: LocalNotebook): Notebook {
    let noteCount = 0;
    for (const n of notes.values()) if (isLive(n) && n.notebookId === nb.id) noteCount++;
    return {
      id: nb.id,
      parentId: nb.parentId,
      name: nb.name,
      noteCount,
      createdAt: nb.createdAt,
      updatedAt: nb.updatedAt,
    };
  }

  /** A live notebook, or null ("No notebook") when it is gone or in the trash. */
  const liveNotebookId = (id: string | null | undefined) => {
    const nb = id ? notebooks.get(id) : undefined;
    return nb && isLive(nb) ? nb.id : null;
  };

  function destroyNote(id: string) {
    const note = notes.get(target(id));
    if (!note) return;
    commit([
      dropNote(note.id),
      queue(note.id, { op: "destroy" }),
      ...conflictsOf(new Set([note.id])),
    ]);
  }

  function destroyNotebook(id: string) {
    const root = notebooks.get(id);
    if (!root?.deletedAt) return;
    const at = root.deletedAt;
    const together = [...notebooks.values()].filter((n) => n.deletedAt === at);
    const ids = new Set([id, ...notebookDescendants(together, id)]);
    const writes: Write[] = [];
    for (const bookId of ids) {
      writes.push(dropNotebook(bookId));
      if (bookId !== id) writes.push(putNotebookEntry(bookId, null));
    }
    const destroyed = new Set<string>();
    for (const note of notes.values()) {
      if (note.deletedAt === at && note.notebookId && ids.has(note.notebookId)) {
        destroyed.add(note.id);
        // The server deletes them with the notebook; nothing of theirs is sent.
        writes.push(dropNote(note.id), putEntry(note.id, null));
      }
    }
    writes.push(queueNotebook(id, { op: "destroy" }));
    commit([...writes, ...conflictsOf(destroyed)]);
  }

  return {
    // --- Lifecycle ---

    async load() {
      const snapshot = await persistence.load();
      meta = (snapshot.meta.get("meta") as Meta | undefined) ?? null;
      for (const table of Object.keys(maps) as Array<keyof typeof maps>) {
        maps[table].clear();
        for (const [key, value] of snapshot[table]) maps[table].set(key, value);
      }
      version++;
      for (const listener of listeners) listener();
    },

    /** Resolves once every change so far is on disk. */
    flushed: () => writing,

    async destroy() {
      await writing;
      meta = null;
      for (const map of [...Object.values(maps), gone, redirects]) map.clear();
      await persistence.destroy();
      version++;
      for (const listener of listeners) listener();
    },

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    version: () => version,

    meta: () => meta,
    setMeta(next: Meta) {
      commit([{ table: "meta", key: "meta", value: next }]);
    },

    // --- Reads for the UI (FR5) ---

    note: (id: string) => notes.get(target(id)),
    notes: () => [...notes.values()],
    notebook: (id: string) => notebooks.get(id),
    /** Follows a note that was re-created under a new id (FR9). */
    resolveId: target,

    listNotes(filter: NotesQuery = {}): NoteSummary[] {
      return liveNotes()
        .filter(
          (n) =>
            (!filter.notebook || n.notebookId === filter.notebook) &&
            (filter.pinned !== "true" || n.pinned),
        )
        .sort(
          (a, b) => Number(b.pinned) - Number(a.pinned) || b.updatedAt.localeCompare(a.updatedAt),
        )
        .map(summary);
    },

    /** Live notebooks with their direct note counts from the local copy, by name. */
    notebookList(): Notebook[] {
      return [...notebooks.values()]
        .filter(isLive)
        .map(notebookWithCount)
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    search: (q: string, notebookId?: string) =>
      searchLocalNotes(texts(), q, notebookId ? { notebookId } : {}),
    titles: (text: string) => matchTitles(texts(), text),

    /** The editor's view of a note: links and backlinks from the local copy. */
    toNote(id: string): Note | null {
      const note = notes.get(target(id));
      if (!note || note.content === null) return null;
      const all = texts();
      return {
        ...summary(note),
        content: note.content,
        version: note.version,
        links: note.kind === "canvas" ? [] : resolveLocalLinks(note.content, all),
        backlinks: localBacklinks(all, note.title, note.id),
      };
    },

    /** FR6a: the trash as the server would list it. */
    trash(): TrashEntry[] {
      return trashEntries([...notebooks.values()], [...notes.values()]);
    },

    syncState(id: string): NoteSyncState {
      const entry = outbox.get(target(id));
      if (!entry) return { state: "synced" };
      return entry.status === "failed"
        ? { state: "failed", error: entry.error ?? "Refused by the server" }
        : { state: "pending" };
    },
    pendingCount: () => outbox.size + notebookOutbox.size,
    conflicts: () => [...conflicts.values()].sort((a, b) => a.at.localeCompare(b.at)),

    // --- Local note changes (FR6) ---

    createNote(fields: {
      kind?: LocalNote["kind"];
      title?: string;
      content?: string;
      notebookId: string | null;
    }) {
      const canvas = fields.kind === "canvas";
      const { note, writes } = newLocalNote({
        kind: fields.kind,
        title: fields.title ?? (canvas ? "Untitled canvas" : ""),
        content: fields.content ?? (canvas ? EMPTY_CANVAS : ""),
        notebookId: liveNotebookId(fields.notebookId),
      });
      commit(writes);
      return note;
    },

    /**
     * Saves the editor's text or strokes. `editorBase` is the version the editor loaded (see
     * `ownBase`). A note that vanished meanwhile is saved as a new note, so nothing typed or
     * drawn is lost (FR9).
     */
    saveContent(id: string, content: string, editorBase: number) {
      const key = target(id);
      const note = notes.get(key);
      if (!note) {
        const last = gone.get(key);
        if (!last) return;
        const copy = newLocalNote({ ...last, content });
        redirects.set(key, copy.note.id);
        commit(copy.writes);
        return;
      }
      const baseVersion = editorBase >= note.ownBase ? note.version : editorBase;
      commit([
        putNote({
          ...note,
          content,
          preview: preview(note.kind, content),
          updatedAt: now().toISOString(),
        }),
        queue(key, { op: "update", fields: { content }, baseVersion }),
      ]);
    },

    updateNote(id: string, fields: Pick<NoteFields, "title" | "pinned" | "notebookId">) {
      const key = target(id);
      const note = notes.get(key);
      if (!note) return undefined;
      const next = { ...note, ...fields, updatedAt: now().toISOString() };
      commit([putNote(next), queue(key, { op: "update", fields })]);
      return next;
    },

    /** To the trash (spec04 FR16); Undo is `restoreNote`. */
    trashNote(id: string) {
      const key = target(id);
      const note = notes.get(key);
      if (!note || !isLive(note)) return;
      commit([
        putNote({ ...note, deletedAt: now().toISOString() }),
        queue(key, { op: "update", fields: { deleted: true } }),
        ...conflictsOf(new Set([key])),
      ]);
    },

    /** Back into its notebook, or "No notebook" if that is gone. */
    restoreNote(id: string) {
      const note = notes.get(target(id));
      if (!note || isLive(note)) return;
      commit([
        putNote({ ...note, deletedAt: null, notebookId: liveNotebookId(note.notebookId) }),
        queue(note.id, { op: "update", fields: { deleted: false } }),
      ]);
    },

    /** Deleted for good from the trash. */
    destroyNote,

    // --- Local notebook changes (FR6, FR6a) ---

    createNotebook(fields: { name: string; parentId: string | null }): Notebook {
      const at = now().toISOString();
      const nb: LocalNotebook = {
        id: options.newId(),
        name: fields.name,
        parentId: liveNotebookId(fields.parentId),
        createdAt: at,
        updatedAt: at,
        deletedAt: null,
      };
      commit([
        putNotebook(nb),
        queueNotebook(nb.id, { op: "create", fields: { name: nb.name, parentId: nb.parentId } }),
      ]);
      return notebookWithCount(nb);
    },

    updateNotebook(id: string, fields: Pick<NotebookFields, "name" | "parentId">) {
      const nb = notebooks.get(id);
      if (!nb || !isLive(nb)) return undefined;
      const next = { ...nb, ...fields, updatedAt: now().toISOString() };
      commit([putNotebook(next), queueNotebook(id, { op: "update", fields })]);
      return notebookWithCount(next);
    },

    /** With its sub-notebooks and their notes, all with one timestamp (spec04 FR7). */
    trashNotebook(id: string) {
      const nb = notebooks.get(id);
      if (!nb || !isLive(nb)) return;
      const at = now().toISOString();
      const live = [...notebooks.values()].filter(isLive);
      const ids = new Set([id, ...notebookDescendants(live, id)]);
      const writes: Write[] = [];
      for (const book of live) {
        if (ids.has(book.id)) writes.push(putNotebook({ ...book, deletedAt: at }));
      }
      const trashed = new Set<string>();
      for (const note of notes.values()) {
        if (isLive(note) && note.notebookId && ids.has(note.notebookId)) {
          trashed.add(note.id);
          writes.push(putNote({ ...note, deletedAt: at }));
        }
      }
      writes.push(queueNotebook(id, { op: "update", fields: { deleted: true } }));
      commit([...writes, ...conflictsOf(trashed)]);
    },

    /** What went to the trash together comes back; under its parent if that is still there. */
    restoreNotebook(id: string) {
      const root = notebooks.get(id);
      if (!root?.deletedAt) return;
      const at = root.deletedAt;
      const together = [...notebooks.values()].filter((n) => n.deletedAt === at);
      const ids = new Set([id, ...notebookDescendants(together, id)]);
      const writes: Write[] = [];
      for (const book of together) {
        if (!ids.has(book.id)) continue;
        const parentId = book.id === id ? liveNotebookId(book.parentId) : book.parentId;
        writes.push(putNotebook({ ...book, deletedAt: null, parentId }));
      }
      for (const note of notes.values()) {
        if (note.deletedAt === at && note.notebookId && ids.has(note.notebookId)) {
          writes.push(putNote({ ...note, deletedAt: null }));
        }
      }
      writes.push(queueNotebook(id, { op: "update", fields: { deleted: false } }));
      commit(writes);
    },

    /** Deleted for good, with what went to the trash together. */
    destroyNotebook,

    /** "Empty trash": everything the device has in its trash, for good. */
    emptyTrash() {
      for (const item of trashEntries([...notebooks.values()], [...notes.values()])) {
        if (item.kind === "notebook") destroyNotebook(item.id);
        else destroyNote(item.id);
      }
    },

    // --- Sync (FR7–FR11) ---

    /** Pending note entries in the order they are sent. */
    pendingEntries: () =>
      [...outbox.values()].filter((e) => e.status === "pending").sort(outboxOrder),
    notebookEntries: () => [...notebookOutbox.values()].sort(outboxOrder),

    /** The server took `sent`; the note takes the server's version. */
    confirmPush(sent: OutboxEntry, server: Note) {
      const note = notes.get(sent.id);
      const current = outbox.get(sent.id);
      const unchanged = current?.rev === sent.rev;
      const writes: Write[] = [];
      if (note) {
        // Fields changed again meanwhile stay as they are on the device.
        const fromServer = unchanged
          ? {
              title: server.title,
              preview: server.preview,
              pinned: server.pinned,
              notebookId: server.notebookId,
              updatedAt: server.updatedAt,
            }
          : {};
        writes.push(
          putNote({ ...note, ...fromServer, version: server.version, createdAt: server.createdAt }),
        );
      }
      if (unchanged) writes.push(putEntry(sent.id, null));
      else if (current && current.op !== "destroy") {
        writes.push(
          putEntry(sent.id, {
            ...current,
            op: "update",
            baseVersion: current.fields.content !== undefined ? server.version : null,
          }),
        );
      }
      commit(writes);
    },

    /** Done without a note to take over (a permanent delete, or a trash change only). */
    settleEntry(sent: OutboxEntry) {
      if (outbox.get(sent.id)?.rev === sent.rev) commit([putEntry(sent.id, null)]);
    },

    /** FR11: refused for good until the user retries. */
    failEntry(sent: OutboxEntry, error: string) {
      const current = outbox.get(sent.id);
      if (current?.rev !== sent.rev) return;
      commit([putEntry(sent.id, { ...current, status: "failed", error })]);
    },

    retry(id: string) {
      const current = outbox.get(id);
      if (current?.status === "failed") {
        commit([putEntry(id, { ...current, status: "pending", error: null })]);
      }
    },

    /**
     * FR8: the server's copy changed since the edit started. The device's text or strokes
     * become a new note; the original drops its edit and loads the server's at the next pull.
     */
    conflict(sent: OutboxEntry): Conflict | null {
      const note = notes.get(sent.id);
      if (!note || note.content === null) return null;
      const copy = newLocalNote({
        kind: note.kind,
        title: conflictTitle(note.title, now(), options.timeZone()),
        content: note.content,
        notebookId: note.notebookId,
        pinned: note.pinned,
      });
      const pair = { originalId: note.id, copyId: copy.note.id, at: now().toISOString() };
      const current = outbox.get(note.id);
      commit([
        ...copy.writes,
        putNote({ ...note, content: null }),
        ...(current?.op === "destroy" ? [] : [putEntry(note.id, null)]),
        { table: "conflicts", key: pair.copyId, value: pair },
      ]);
      return pair;
    },

    /** FR9: gone on the server; an edit of its content or title lives on as a new note. */
    recreate(sent: OutboxEntry) {
      const note = notes.get(sent.id);
      const writes: Write[] = [putEntry(sent.id, null), dropNote(sent.id)];
      if (note && note.content !== null) {
        const copy = newLocalNote({ ...note, content: note.content });
        redirects.set(note.id, copy.note.id);
        writes.push(...copy.writes);
      }
      if (note) gone.set(note.id, note);
      commit(writes);
    },

    /**
     * FR11 "Save as new note": the refused text becomes a new note, and the original loads
     * the server's text again at the next sync. Returns the new note's id.
     */
    saveAsNew(id: string): string | null {
      const note = notes.get(id);
      const entry = outbox.get(id);
      if (!note || entry?.op !== "update" || note.content === null) return null;
      const copy = newLocalNote({ ...note, content: note.content });
      commit([...copy.writes, putEntry(id, null), putNote({ ...note, content: null })]);
      return copy.note.id;
    },

    /** A notebook entry is done (sent, or dropped after the server refused it: FR9). */
    settleNotebook(sent: NotebookEntry) {
      if (notebookOutbox.get(sent.id)?.rev === sent.rev) commit([putNotebookEntry(sent.id, null)]);
    },

    /** One page of `GET /api/notes/sync` (FR7 step 3). */
    applyPull(page: NotesSync) {
      const writes: Write[] = [];
      const known = new Set(page.index.map((n) => n.id));
      const bookIds = new Set(page.notebooks.map((n) => n.id));
      const changed = new Map(page.notes.map((n) => [n.id, n]));

      for (const nb of page.notebooks) {
        if (!notebookOutbox.has(nb.id)) writes.push(putNotebook(nb));
      }
      for (const nb of notebooks.values()) {
        if (!bookIds.has(nb.id) && !notebookOutbox.has(nb.id)) writes.push(dropNotebook(nb.id));
      }

      for (const entry of page.index) {
        if (outbox.has(entry.id)) continue; // the device's change wins until it is sent
        const local = notes.get(entry.id);
        const fresh = changed.get(entry.id);
        const foreign = !local || local.version !== entry.version;
        let content = local?.content ?? null;
        if (fresh && fresh.version >= entry.version) content = fresh.content;
        else if (foreign) content = null; // loaded after the pages (sync.ts)
        writes.push(
          putNote({
            ...entry,
            content,
            ownBase: foreign ? entry.version : (local?.ownBase ?? entry.version),
          }),
        );
      }

      const removed = new Set<string>();
      for (const note of notes.values()) {
        if (known.has(note.id) || outbox.has(note.id)) continue;
        gone.set(note.id, note);
        removed.add(note.id);
        writes.push(dropNote(note.id));
      }
      commit([...writes, ...conflictsOf(removed)]);
    },

    /** Live notes whose content must be loaded one by one. */
    missingContent: () => liveNotes().filter((n) => n.content === null && !outbox.has(n.id)),

    applyContent(server: Note) {
      const local = notes.get(server.id);
      if (!local || outbox.has(server.id) || server.version < local.version) return;
      commit([
        putNote({
          ...local,
          content: server.content,
          version: server.version,
          ownBase: server.version === local.version ? local.ownBase : server.version,
        }),
      ]);
    },

    setLastSync(serverTime: string) {
      if (meta) commit([{ table: "meta", key: "meta", value: { ...meta, lastSync: serverTime } }]);
    },

    /** FR10: "Keep both", or after the copy or original went away. */
    dismissConflict(copyId: string) {
      commit([dropConflict(copyId)]);
    },
  };
}

export type OfflineStore = ReturnType<typeof createOfflineStore>;
